// 主人公の回避の長さ・歩きの足運びを見比べる版 (2026-10-06・DEV ?dodgeLen=b|c|d&walkPace=b|c・残像 ?dodgeTrail=1|plain|dark|violet|white)。
//   10-05 ユーザー「回避は全体のフレームをもっと長くしてもいいと思っている（回避楽という感想を経て。ただ、連続回避
//   できないようにした分と合わせてうまく調整が必要かも）。現状の回避のスピードはコマ送りが早すぎてバタついているように
//   見える」「歩きもそうだな。パタパタ歩いてる感じする」→ 今の版と並べて選んでもらう。
//   回避の既定は d (10-07 採用・旧 a = ?dodgeLen=a)。 歩きの既定は b (10-06 採用)。 無敵 (既定 200ms＝PLAYER.stepIFrameMs・10-14 に戻した。10-07〜10-14 は 100ms) と距離 (stepDistance 90)・
//   連続回避の制限 (stepCooldownMs 500) は全版同じ。
//   アニメは scripts/author_player_study_motions.mjs (dodge_b / dodge_c) と scripts/polish_player_motions.ts (walk_b / walk_c …)。
import { PLAYER } from './balance'
import { hatsuenDodgeIFrameFloorMs } from './difficulty'
import { lunaWalkSpeedOverride } from './player_walk_new'

export type LocomotionVariant = 'a' | 'b' | 'c'
export type DodgeVariantKey = LocomotionVariant | 'd'

interface DodgeVariant {
  /** 回避の長さ (ms)。 この間は攻撃・歩き・回避を出せない (パリィは無敵が明けた後に出せる) */
  durationMs: number
  /** Spine のアニメ名 */
  anim: string
  /** 移動の速さの付け方。 linear = 等速 (今)・easeOut = 最初に大きく進んで着地へ減速する */
  travel: 'linear' | 'easeOut'
  /** 移動にかける時間 (ms)。 省略 = 回避の長さ全部。 短いと、移動し終えた後は止まって起き上がる */
  travelMs?: number
  /** 回避が明けた後も、何も操作しなければアニメの残り (低く止まって立つ) を見せる長さ (ms)。 描くだけ = 操作はすぐ受ける */
  settleMs?: number
  /** 描く位置だけ「最初に大きく進んで減速する」ように先へ出す (当たりの位置は travel のまま) */
  visualEaseOut?: boolean
}

export const DODGE_VARIANTS: Record<DodgeVariantKey, DodgeVariant> = {
  a: { durationMs: PLAYER.stepDurationMs, anim: 'dodge', travel: 'linear' },
  b: { durationMs: 360, anim: 'dodge_b', travel: 'easeOut' },
  c: { durationMs: 460, anim: 'dodge_c', travel: 'easeOut' },
  // 10-06 ユーザーが実在の回避から選んだ「D1 Bloodborne のステップ・D6 Lies of P の回避・D4 ゼンゼロの回避」の形:
  //   0.2 s で 90px を一気に滑り (= 無敵と同じ長さ)、 低い姿勢で止まって立ち上がる (アニメ全体 0.56 s)。
  //   10-07 最初は 0.56 s 全部動けなくした → 自動プレイで介錯の勝ち 7/20 → 1/20 (体をすり抜けた後の小突きを受ける)。
  //   ユーザー「回避フレーム数はどちらかと言うと減らす方針…モーションに必要なフレーム数は増えてもいい」
  //   → 動けない時間は今の回避と同じ 260 ms・止まって立つ残り 300 ms は描くだけ (歩く・攻撃・回避・パリィで即切れる)。
  //   体の位置 (当たり) は今の回避と同じ 260 ms 等速。 減速する滑り (0.2 s で約 95%) は描く位置だけ (dodgeVisualLeadPx・最大 22px 先)。
  //   当たりまで減速にすると、自動プレイの間合いが変わって覚醒王・騎士のテスト3件が赤になった (騎士の勝ち 0.75 → 0.25)。
  d: { durationMs: PLAYER.stepDurationMs, anim: 'dodge_d', travel: 'linear', settleMs: 300, visualEaseOut: true },
}

interface WalkVariant {
  /** 歩く速さの倍率 (PLAYER.moveSpeed に掛ける) */
  speedMul: number
  /** Spine のアニメ名の後ろに付ける (walk / walk_back) */
  suffix: string
}

export const WALK_VARIANTS: Record<LocomotionVariant, WalkVariant> = {
  a: { speedMul: 1, suffix: '' },      // 0.8 s で2歩・120 px/s
  b: { speedMul: 1, suffix: '_b' },    // 1.0 s で2歩・大股 (速さは同じ)
  c: { speedMul: 0.8, suffix: '_c' },  // 1.0 s で2歩・歩幅は今のまま・96 px/s
}

// 回避は 10-07 ユーザーが D を実機で触り「D＋黒い影を既定に」= 既定 d (旧 = ?dodgeLen=a・PLAYER.stepDurationMs 260)
let dodgeVariant: DodgeVariantKey = 'd'
// 歩きは 10-06 ユーザーが動画を見て「B 大股でゆっくり」を選んだ = 既定 b (旧 = ?walkPace=a)
let walkVariant: LocomotionVariant = 'b'

const isVariant = (v: string | null): v is LocomotionVariant => v === 'a' || v === 'b' || v === 'c'
export function setDodgeVariant(v: string | null): void { if (isVariant(v) || v === 'd') dodgeVariant = v }
export function setWalkVariant(v: string | null): void { if (isVariant(v)) walkVariant = v }
export function currentDodgeVariant(): DodgeVariant {
  const v = DODGE_VARIANTS[dodgeVariant]
  return dodgeVariant === 'd' && dodgePose !== 'plain' ? { ...v, anim: `dodge_d_${dodgePose}` } : v
}
// 回避 d の姿の見比べ (DEV ?dodgePose=lop|zzz・10-07「回避の絵のブラッシュアップを進めて」)。 動き・当たりは d と同じで、アニメだけ替える。
//   lop = Lies of P 寄り (進む方向へ上体ごと倒れ込む・後ろの腕を大きく伸ばす)・zzz = ゼンゼロ寄り (片膝が床に付くほど深く沈む)・plain = 最初の d (膝で沈んでまっすぐ滑る)
//   10-07 ユーザー (3案の動画を見て)「Lies of P 寄りで」= 既定 lop
export type DodgePose = 'lop' | 'zzz' | 'plain'
let dodgePose: DodgePose = 'lop'
export function setDodgePose(v: string | null): void { if (v === 'lop' || v === 'zzz' || v === 'plain') dodgePose = v }
export function currentWalkVariant(): WalkVariant { return WALK_VARIANTS[walkVariant] }

export function stepDurationMs(): number { return DODGE_VARIANTS[dodgeVariant].durationMs }
/** 回避中、描く位置を体の位置より進行方向へ先に出す量 (px)。 elapsedSec = 回避の経過・dir = 進む向き */
export function dodgeVisualLeadPx(elapsedSec: number, dir: number): number {
  const v = DODGE_VARIANTS[dodgeVariant]
  if (!v.visualEaseOut) return 0
  const t = Math.max(0, Math.min(1, elapsedSec * 1000 / v.durationMs))
  return dir * PLAYER.stepDistance * ((1 - (1 - t) * (1 - t)) - t)
}
// 回避の無敵の長さ (DEV ?dodgeIFrame=a|b|c・10-07 ユーザー「無敵を短くした版も作って比べたい」・方針「回避フレーム数はどちらかと言うと減らす」)。
//   a = 今 200ms (PLAYER.stepIFrameMs)・b = 150ms・c = 100ms。 回避の動き・距離・動けない時間 (260ms) は同じ。
//   無敵が明けたらパリィで回避を取り消せる (今と同じ決まり) ので、短いほど早くパリィも出せる。
//   ⚠️ 弾けない赤の当たり 200ms (騎士の lanceThrust・刃踊りの pirouette・元王女の mirrorThrust 等) は無敵だけでは跨げなくなり、
//      押す時機と下がる距離で避けることになる (attacks.ts「赤の activeMs は stepIFrameMs 以下」の決まりは a の値で書いてある)。
export const DODGE_IFRAME_VARIANTS: Record<LocomotionVariant, number> = { a: PLAYER.stepIFrameMs, b: 150, c: 100 }
// 10-07 ユーザー「無敵をかなり短めでOK」→「赤い攻撃の当たる瞬間を短くする」。 c にすると弾けない赤の当たりも
//   届いてから無敵の長さまでに詰まる (hits.ts spawnMeleeHit)。
// 🔵 10-07 既定を c (100ms) へ: 検収パック (docs/playtest/packs/2026-10-07-dodge-iframe-100-pack.md) で 6ボスとも
//   「赤を回避で抜けられる・ちょうどいい」・V-1「全ボス100msでよい」。 旧 200ms = DEV ?dodgeIFrame=a。
//   PLAYER.stepIFrameMs (200) は旧の値＝赤の当たりを詰めるかの基準として残す。
// 🔵 10-14 既定を a (200ms) へ戻した: 負け画面の Tips 式の検収 (kensyu-defeat-tips v2) でユーザー「回避はもう少し楽にして良い
//   （回避フレームを小さくする前の数値に戻す）。パリィを使わないと倒すのに時間はかかるし、適当に回避し続けることは現状でもできない」。
//   10-07〜10-14 の 100ms = DEV ?dodgeIFrame=c (150ms = b)。 a では赤の当たりの寿命の詰め (hits.ts) も掛からない＝10-07 より前と同じ。
//   記録 = docs/research/2026-10-14-defeat-tips-v2-answers.md
let dodgeIFrame: LocomotionVariant = 'a'
export function setDodgeIFrameVariant(v: string | null): void { if (isVariant(v)) dodgeIFrame = v }
export function stepIFrameMs(): number { return DODGE_IFRAME_VARIANTS[dodgeIFrame] }
/** 主人公が実際に無敵でいる長さ (ms)。 通常 = stepIFrameMs() と同じ。 DEV ?hatsuenTime=1 の初演だけ 150ms 以上へ広げる (src/difficulty.ts)。
 *  ⚠️ ボス側 (hits.ts の赤の当たりの寿命の詰め) は広げない = stepIFrameMs() を使い続ける。 救済は主人公の側だけで作る。 */
export function playerStepIFrameMs(): number { return Math.max(stepIFrameMs(), hatsuenDodgeIFrameFloorMs()) }
export function dodgeSettleMs(): number { return DODGE_VARIANTS[dodgeVariant].settleMs ?? 0 }
export function dodgeTravelMs(): number { return DODGE_VARIANTS[dodgeVariant].travelMs ?? DODGE_VARIANTS[dodgeVariant].durationMs }

// 回避の残像 (DEV ?dodgeTrail=1・10-06 D4 ゼンゼロの回避の「光と青い残像」を足すか見比べる)。 描くのは render.ts drawDodgeTrail
//   10-07 ユーザー (D を実機で)「残像ありなんだけど、青いのはへんだな」→ 色の候補を並べる:
//   ?dodgeTrail=plain (色を付けず本体の色のまま薄く)・dark (黒い影)・violet (髪の毛先の紫)・white (白)・blue (最初の版)。 → 「黒い影で」= 1 も黒い影
export type DodgeTrailStyle = 'blue' | 'plain' | 'dark' | 'violet' | 'white'
//   10-07「黒い影で」→「D＋黒い影を既定に」= 既定で黒い影を出す。 ?dodgeTrail=0 で消す。
let dodgeTrail: DodgeTrailStyle | null = 'dark'
export function setDodgeTrail(v: string | null): void {
  if (v === null) return
  dodgeTrail = v === '1' || v === 'dark' ? 'dark' : v === 'blue' ? 'blue'
    : v === 'plain' || v === 'violet' || v === 'white' ? v : null
}
export function dodgeTrailOn(): boolean { return dodgeTrail !== null }
export function dodgeTrailStyle(): DodgeTrailStyle { return dodgeTrail ?? 'dark' }
// DEV ?lunaWalk=new|newslow|newspeed (2026-10-12・src/player_walk_new.ts) の時だけ速さを上書きする。 指定なし = 今の速さ
export function walkSpeedPxPerSec(): number { return lunaWalkSpeedOverride() ?? PLAYER.moveSpeed * WALK_VARIANTS[walkVariant].speedMul }

/** 回避の進み具合 (0〜1)。 s = 回避の経過 / 移動にかける時間 */
export function dodgeTravelProgress(s: number): number {
  const t = Math.max(0, Math.min(1, s))
  return DODGE_VARIANTS[dodgeVariant].travel === 'linear' ? t : 1 - (1 - t) * (1 - t)
}
